/**
 * Portal dynamic-offer overlay — fetch treatment/control quotes for SKUs.
 * Prices and tokens come only from the API proxy; never invent discounts client-side.
 */

const ANON_KEY = "bookers.anonymousId";
const SESSION_KEY = "bookers.offerSessionId";
/** List → details handoff so item page shows the same offer price as the card. */
const HANDOFF_KEY = "bookers.offerHandoff";
const HANDOFF_TTL_MS = 30 * 60 * 1000;

function createId(prefix) {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `${prefix}_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 10)}`;
}

export function getOrCreateAnonymousId() {
    try {
        if (typeof localStorage === "undefined") {
            return createId("anon").slice(0, 120);
        }
        const existing = localStorage.getItem(ANON_KEY);
        if (existing && existing.length > 0) {
            return existing.slice(0, 120);
        }
        const id = createId("anon").slice(0, 120);
        localStorage.setItem(ANON_KEY, id);
        return id;
    } catch (e) {
        return createId("anon").slice(0, 120);
    }
}

export function getOrCreateOfferSessionId() {
    try {
        if (typeof sessionStorage === "undefined") {
            return createId("sess").slice(0, 120);
        }
        const existing = sessionStorage.getItem(SESSION_KEY);
        if (existing && existing.length > 0) {
            return existing.slice(0, 120);
        }
        const id = createId("sess").slice(0, 120);
        sessionStorage.setItem(SESSION_KEY, id);
        return id;
    } catch (e) {
        return createId("sess").slice(0, 120);
    }
}

function mapLocale(lang) {
    if (lang === "pt" || lang === "br") return "pt-BR";
    if (["en", "es", "pt-BR", "fr"].includes(lang)) return lang;
    return "en";
}

/**
 * @param {object} opts
 * @param {string[]} [opts.skuIds]
 * @param {string[]} [opts.skuCodes]
 * @param {string} [opts.lang]
 * @param {string} [opts.channel]
 * @param {string} [opts.trigger]
 * @returns {Promise<Record<string, object>>} map keyed by sku_id
 */
export async function fetchOfferOverlays(opts = {}) {
    const skuIds = (opts.skuIds || []).filter(Boolean).slice(0, 50);
    const skuCodes = (opts.skuCodes || []).filter(Boolean).slice(0, 50);
    if (!skuIds.length && !skuCodes.length) {
        return {};
    }

    try {
        const response = await window.axios.post("/api/offers/overlay", {
            sku_ids: skuIds.length ? skuIds : undefined,
            sku_codes: skuCodes.length ? skuCodes : undefined,
            channel: opts.channel || "SHOP",
            locale: mapLocale(opts.lang || "en"),
            anonymous_id: getOrCreateAnonymousId(),
            session_id: getOrCreateOfferSessionId(),
            trigger: opts.trigger || undefined,
            utm_source: opts.utm_source || undefined,
            utm_medium: opts.utm_medium || undefined,
            utm_campaign: opts.utm_campaign || undefined
        });

        const rows = (response && response.data && response.data.data) || [];
        const bySku = {};
        rows.forEach(row => {
            if (row && row.sku_id) {
                bySku[row.sku_id] = row;
            }
        });
        return bySku;
    } catch (e) {
        return {};
    }
}

/**
 * Priority 1: treatment offer deal; Priority 2: catalog deal.
 * @param {object|null} overlay
 * @param {{ code?: number, title?: string, text?: string }} catalogFlag
 * @param {boolean} [allowCatalogDeals=true]
 */
export function resolveDisplayFlag(overlay, catalogFlag, allowCatalogDeals = true) {
    const isTreatment =
        overlay &&
        overlay.experiment_group === "treatment" &&
        overlay.deal &&
        (overlay.deal.title || overlay.deal.description);

    if (isTreatment) {
        return {
            code: Number(overlay.deal.icon_code) || 2,
            title: overlay.deal.title || "",
            text: overlay.deal.description || ""
        };
    }

    if (allowCatalogDeals === false) {
        return { code: 0, title: "", text: "" };
    }

    return {
        code: Number((catalogFlag && catalogFlag.code) || 0),
        title: (catalogFlag && catalogFlag.title) || "",
        text: (catalogFlag && catalogFlag.text) || ""
    };
}

/**
 * @param {object|null} overlay
 * @returns {{ base: number|null, offer: number|null }|null}
 */
export function resolveOfferPricing(overlay) {
    if (
        !overlay ||
        overlay.experiment_group !== "treatment" ||
        !overlay.pricing
    ) {
        return null;
    }
    const base = Number(overlay.pricing.base_price_amount);
    const offer = Number(overlay.pricing.offer_price_amount);
    if (!Number.isFinite(offer)) {
        return null;
    }
    return {
        base: Number.isFinite(base) ? base : null,
        offer
    };
}

/** Treatment-only token — drives deal UI / has_offer. */
export function resolveOfferToken(overlay) {
    if (
        !overlay ||
        overlay.experiment_group !== "treatment" ||
        !overlay.token
    ) {
        return null;
    }
    return String(overlay.token);
}

/**
 * Token for Shop attribution (treatment deal OR silent control).
 * Control gets a signed token without deal pricing so cart events can be counted.
 * @param {object|null} overlay
 * @returns {string|null}
 */
export function resolveAttributionToken(overlay) {
    if (!overlay || !overlay.token) {
        return null;
    }
    const group = String(overlay.experiment_group || "");
    if (group !== "treatment" && group !== "control") {
        return null;
    }
    return String(overlay.token);
}

/**
 * Assignment id for deal CTAs / offer_click (treatment only).
 * @param {object|null} overlay
 * @returns {string|null}
 */
export function resolveOfferAssignmentId(overlay) {
    if (
        !overlay ||
        overlay.experiment_group !== "treatment" ||
        !overlay.assignment_id
    ) {
        return null;
    }
    return String(overlay.assignment_id);
}

/**
 * Fire-and-forget funnel click → Portal proxy → OLS POST /ols/offers/events.
 * Never blocks UX for more than a short timeout; failures are silent.
 *
 * @param {object} opts
 * @param {string} [opts.assignmentId]
 * @param {string} [opts.source]
 * @returns {Promise<boolean>}
 */
export async function recordOfferClick(opts = {}) {
    const assignmentId = opts.assignmentId
        ? String(opts.assignmentId).trim()
        : "";
    if (!assignmentId) {
        return false;
    }

    try {
        await window.axios.post(
            "/api/offers/events",
            {
                event_type: "offer_click",
                assignment_id: assignmentId,
                anonymous_id: getOrCreateAnonymousId(),
                session_id: getOrCreateOfferSessionId(),
                source: opts.source || "portal_cta"
            },
            { timeout: 2500 }
        );
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Persist list → details attribution.
 * Treatment: keeps deal price + token for UI/buy.
 * Control: silent token only (no pricing / no deal UI).
 * @param {object} payload
 * @param {string} [payload.sku_id]
 * @param {string} [payload.hash]
 * @param {string} payload.token
 * @param {string} [payload.assignment_id]
 * @param {'treatment'|'control'} [payload.experiment_group='treatment']
 * @param {number} [payload.offer_price_amount] required for treatment
 * @param {number|null} [payload.base_price_amount]
 * @param {number|null} [payload.discount_percent]
 */
export function saveOfferHandoff(payload = {}) {
    const token = payload.token ? String(payload.token) : "";
    if (!token) {
        return;
    }
    const group =
        payload.experiment_group === "control" ? "control" : "treatment";
    const offer = Number(payload.offer_price_amount);
    if (group === "treatment" && !Number.isFinite(offer)) {
        return;
    }
    try {
        if (typeof sessionStorage === "undefined") {
            return;
        }
        sessionStorage.setItem(
            HANDOFF_KEY,
            JSON.stringify({
                sku_id: payload.sku_id ? String(payload.sku_id) : null,
                hash: payload.hash ? String(payload.hash) : null,
                token,
                assignment_id: payload.assignment_id
                    ? String(payload.assignment_id)
                    : null,
                experiment_group: group,
                pricing:
                    group === "treatment"
                        ? {
                              base_price_amount: Number.isFinite(
                                  Number(payload.base_price_amount)
                              )
                                  ? Number(payload.base_price_amount)
                                  : null,
                              offer_price_amount: offer,
                              discount_percent: Number.isFinite(
                                  Number(payload.discount_percent)
                              )
                                  ? Number(payload.discount_percent)
                                  : null
                          }
                        : null,
                at: Date.now()
            })
        );
    } catch (e) {
        // ignore quota / private mode
    }
}

/**
 * @param {{ sku_id?: string|null, hash?: string|null }} match
 * @returns {object|null} overlay-shaped object for resolveOfferPricing / token
 */
export function readOfferHandoff(match = {}) {
    try {
        if (typeof sessionStorage === "undefined") {
            return null;
        }
        const raw = sessionStorage.getItem(HANDOFF_KEY);
        if (!raw) {
            return null;
        }
        const data = JSON.parse(raw);
        if (
            !data ||
            (data.experiment_group !== "treatment" &&
                data.experiment_group !== "control")
        ) {
            return null;
        }
        if (!data.at || Date.now() - Number(data.at) > HANDOFF_TTL_MS) {
            sessionStorage.removeItem(HANDOFF_KEY);
            return null;
        }
        const matchSku = match.sku_id ? String(match.sku_id) : "";
        const matchHash = match.hash ? String(match.hash) : "";
        const sameSku = matchSku && data.sku_id && matchSku === String(data.sku_id);
        const sameHash = matchHash && data.hash && matchHash === String(data.hash);
        if (!sameSku && !sameHash) {
            return null;
        }
        if (data.experiment_group === "treatment") {
            if (!resolveOfferPricing(data) || !resolveOfferToken(data)) {
                return null;
            }
            return data;
        }
        // Control: attribution token only — no deal pricing.
        if (!resolveAttributionToken(data)) {
            return null;
        }
        return data;
    } catch (e) {
        return null;
    }
}

/**
 * Commercial argument copy for the offer card.
 * Hierarchy: title (headline) + description (supporting body).
 * Source is overlay.deal (composed from the primary commercial argument).
 * @param {object|null} overlay
 * @returns {{ title: string, description: string }}
 */
export function resolveOfferHighlights(overlay) {
    const empty = { title: "", description: "" };

    if (!overlay || overlay.experiment_group !== "treatment") {
        return empty;
    }

    const stripHtml = value =>
        String(value || "")
            .replace(/<[^>]+>/g, " ")
            .replace(/\s+/g, " ")
            .trim();

    const deal = overlay.deal || {};
    let title = deal.title ? String(deal.title).trim() : "";
    let description = stripHtml(deal.description);

    if (!title && !description) {
        const primary = (overlay.arguments || [])[0];
        if (!primary) {
            return empty;
        }
        title = primary.title ? String(primary.title).trim() : "";
        description = stripHtml(primary.description);
    }

    if (description && description === title) {
        description = "";
    }

    return { title, description };
}

/**
 * Short urgency badge on the ticket card (Console Marketing urgency_label).
 * @param {object|null} overlay
 * @returns {string|null}
 */
export function resolveOfferUrgencyLabel(overlay) {
    if (!overlay || overlay.experiment_group !== "treatment") {
        return null;
    }

    if (overlay.deal && overlay.deal.urgency_label) {
        const fromDeal = String(overlay.deal.urgency_label).trim();
        if (fromDeal) return fromDeal;
    }

    for (const row of overlay.arguments || []) {
        const label =
            row && row.urgency_label ? String(row.urgency_label).trim() : "";
        if (label) return label;
    }

    return null;
}
